import type { ComputedRef, WritableComputedRef } from 'vue';
import type { Nullable } from '../../types';
import type { SelectOption } from './TSelect.utils';
import { ref } from 'vue';
import { getInitialHighlightedIndex } from './TSelect.utils';

const SCROLL_STEP_PX = 36;

export function useSelectDropdown(
  options: ComputedRef<SelectOption[]>,
  internalValue: WritableComputedRef<SelectOption['value']>,
  disabled: ComputedRef<boolean>,
) {
  const menuOpen = ref(false);
  const highlightedIndex = ref(-1);
  const wrapperRef = ref<Nullable<HTMLDivElement>>(null);
  const triggerRef = ref<Nullable<HTMLButtonElement>>(null);
  const menuRef = ref<Nullable<HTMLUListElement>>(null);
  const menuHovered = ref(false);
  const isInteractingWithMenu = ref(false);

  const openMenu = () => {
    if (disabled.value || !options.value.length) {
      return;
    }
    menuOpen.value = true;
    highlightedIndex.value = getInitialHighlightedIndex(
      options.value,
      internalValue.value,
    );
  };

  const closeMenu = () => {
    menuOpen.value = false;
    highlightedIndex.value = -1;
    menuHovered.value = false;
    isInteractingWithMenu.value = false;
  };

  const scrollMenuBy = (direction: 1 | -1) => {
    const el = menuRef.value;
    if (!el) {
      return;
    }
    el.scrollBy({ top: direction * SCROLL_STEP_PX, behavior: 'smooth' });
  };

  const selectOption = (option: SelectOption) => {
    if (option.disabled) {
      return;
    }
    internalValue.value = option.value;
    closeMenu();
  };

  const handleClickTrigger = () => {
    if (menuOpen.value) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  const handleBlur = () => {
    requestAnimationFrame(() => {
      if (isInteractingWithMenu.value) {
        return;
      }
      const activeElement = document.activeElement;
      if (!wrapperRef.value?.contains(activeElement)) {
        closeMenu();
      }
    });
  };

  const handleMenuInteractionStart = () => {
    isInteractingWithMenu.value = true;
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (disabled.value) {
      return;
    }

    if (
      !menuOpen.value
      && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)
    ) {
      event.preventDefault();
      openMenu();
      return;
    }

    if (!menuOpen.value) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      scrollMenuBy(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      scrollMenuBy(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = options.value[highlightedIndex.value];
      if (option) {
        selectOption(option);
      }
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      closeMenu();
    }
  };

  return {
    menuOpen,
    highlightedIndex,
    wrapperRef,
    triggerRef,
    menuRef,
    menuHovered,
    isInteractingWithMenu,
    openMenu,
    closeMenu,
    selectOption,
    handleClickTrigger,
    handleBlur,
    handleMenuInteractionStart,
    handleKeydown,
    scrollMenuBy,
  };
}
